import { useEffect, useRef, useState } from "react";

export interface RemotePlayer {
  userId: string;
  username: string;
  avatarId: string;
  locationId: string;
  topBadges: string[];
}

// ─── Badge helpers ─────────────────────────────────────────────────────────────

const BADGE_CATS: Record<string, "A" | "B" | "C"> = {
  mahtit: "A", diedalas: "A", mahttalas: "A",
  duojars: "B", meaccar: "B",
  matkeolmmaj: "C", mailmmeolmmaj: "C",
};

// Ordered easy → hard; we iterate in reverse to find highest per category
const BADGE_ORDER = [
  "mahtit", "diedalas", "mahttalas",
  "duojars", "meaccar",
  "matkeolmmaj", "mailmmeolmmaj",
];

function computeTopBadges(earnedIds: string[]): string[] {
  const earned = new Set(earnedIds);
  const result: string[] = [];
  const seenCat = new Set<string>();
  for (let i = BADGE_ORDER.length - 1; i >= 0; i--) {
    const id = BADGE_ORDER[i];
    const cat = BADGE_CATS[id];
    if (earned.has(id) && !seenCat.has(cat)) {
      result.push(id);
      seenCat.add(cat);
    }
  }
  return result;
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

type PlayersUpdate = { type: "players_update"; players: RemotePlayer[] };

/**
 * Connects to the MapRoom Durable Object shared WebSocket.
 * - Sends `init` on connect with the player's current location and top badges.
 * - Sends `move` whenever `locationId` changes.
 * - Returns the list of ALL players (including self) so the map can render them;
 *   callers filter out self if needed.
 */
export function useMapRoom(
  token: string | null,
  userId: string | undefined,
  locationId: string,
  earnedBadgeIds: string[]
): RemotePlayer[] {
  const [players, setPlayers] = useState<RemotePlayer[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const locationRef = useRef(locationId);
  const badgesRef = useRef(earnedBadgeIds);

  // Keep refs current
  useEffect(() => { locationRef.current = locationId; }, [locationId]);
  useEffect(() => { badgesRef.current = earnedBadgeIds; }, [earnedBadgeIds]);

  // Send move when location changes while connected
  useEffect(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "move", locationId }));
    }
  }, [locationId]);

  useEffect(() => {
    if (!token || !userId) return;

    // Derive WebSocket URL from the same API base as the HTTP client.
    // In dev: /ws/map → relative (proxied by Vite to localhost:8787)
    // In prod: https://my-worker.workers.dev → wss://my-worker.workers.dev/ws/map
    const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";
    let wsUrl: string;
    if (apiBase.startsWith("http")) {
      wsUrl = apiBase.replace(/^http/, "ws") + `/ws/map?token=${encodeURIComponent(token)}`;
    } else {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      wsUrl = `${protocol}//${window.location.host}/ws/map?token=${encodeURIComponent(token)}`;
    }

    let ws: WebSocket;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let destroyed = false;

    function connect() {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        ws.send(
          JSON.stringify({
            type: "init",
            locationId: locationRef.current,
            topBadges: computeTopBadges(badgesRef.current),
          })
        );
      });

      ws.addEventListener("message", (event) => {
        let msg: PlayersUpdate;
        try {
          msg = JSON.parse(event.data as string) as PlayersUpdate;
        } catch {
          return;
        }
        if (msg.type === "players_update") {
          setPlayers(msg.players);
        }
      });

      ws.addEventListener("close", () => {
        wsRef.current = null;
        if (!destroyed) reconnectTimer = setTimeout(connect, 3000);
      });

      ws.addEventListener("error", () => ws.close());
    }

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      ws?.close();
      wsRef.current = null;
      setPlayers([]);
    };
  // Reconnect only when token changes (userId won't change without a new token)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  return players;
}
